import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useHome } from '../hooks/useHome';
import { useExperienceList } from '../hooks/useExperienceList';
import { useHomeFilters, type HomeFilters } from '../hooks/useHomeFilters';
import type { ExperienceListItem, HomeResponse } from '../types';

interface HomeDataContextValue {
  home: HomeResponse | null;
  homeLoading: boolean;
  homeError: Error | null;
  allExperiences: ExperienceListItem[] | null;
  filters: HomeFilters;
  setCity: (slug: string | null) => void;
  toggleTag: (slug: string) => void;
  removeTag: (slug: string) => void;
  clearAll: () => void;
}

const HomeDataContext = createContext<HomeDataContextValue | null>(null);

/**
 * Single source of truth for home data + URL-driven filter state. Provided
 * high in the tree so the Header (filter dropdowns) and the HomePage body
 * share one fetch and one URL-state listener — no duplicate network calls,
 * no diverging local state.
 */
export function HomeDataProvider({ children }: { children: ReactNode }) {
  const { data: home, loading: homeLoading, error: homeError } = useHome();
  const { data: allExperiences } = useExperienceList();
  const { filters, setCity, toggleTag, removeTag, clearAll } = useHomeFilters();

  const value: HomeDataContextValue = {
    home,
    homeLoading,
    homeError,
    allExperiences,
    filters,
    setCity,
    toggleTag,
    removeTag,
    clearAll,
  };

  return <HomeDataContext.Provider value={value}>{children}</HomeDataContext.Provider>;
}

export function useHomeData(): HomeDataContextValue {
  const ctx = useContext(HomeDataContext);
  if (!ctx) throw new Error('useHomeData must be used inside <HomeDataProvider>');
  return ctx;
}
